/**
 * 👥 ENHANCED USERS TAB V2 - Mission Critical
 * 
 * Complete user management with CMT sync integration:
 * - Multi-CMT role display
 * - Bulk operations with checkboxes
 * - Invitation status tracking
 * - Sync status indicators
 * 
 * SAFETY FEATURES:
 * - Comprehensive error handling
 * - Bulk operation confirmations
 * - Status validation
 * - Audit logging
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Tooltip, 
  Modal, 
  Alert,
  Input,
  Select,
  Checkbox,
  Progress,
  Divider,
  Badge,
  message
} from 'antd';
import { 
  UserAddOutlined, 
  MailOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  SearchOutlined,
  FilterOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  SendOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import cloudUserService from '../../services/cloudUserService';
import { cmtService } from '../../services/cmtService';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const EnhancedUsersTabV2 = ({ selectedOrgId, selectedOrgName, selectedOrgData, refreshTick }) => {
  
  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cmtFilter, setCmtFilter] = useState('all');
  
  // Selection and bulk operations
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkOperationLoading, setBulkOperationLoading] = useState(false);
  const [bulkInviteModalVisible, setBulkInviteModalVisible] = useState(false);
  
  // User actions
  const [actionLoading, setActionLoading] = useState(false);
  const [invitingUserIds, setInvitingUserIds] = useState(new Set());
  const [cooldownUserIds, setCooldownUserIds] = useState(new Set());
  
  // CMT reference and fallback support
  const [cmtIdToName, setCmtIdToName] = useState({});
  const [orgFlattenedMembers, setOrgFlattenedMembers] = useState([]);
  const backfilledUserIdsRef = useRef(new Set());

  const addInviting = (userId) => {
    setInvitingUserIds(prev => {
      const next = new Set(prev);
      next.add(userId);
      return next;
    });
  };

  const removeInviting = (userId) => {
    setInvitingUserIds(prev => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  /**
   * 📥 Load all users for the selected organization
   */
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      console.log(`👥 Loading users for org: ${selectedOrgId}`);
      
      const usersRef = collection(db, 'c3_users');
      const q = query(
        usersRef, 
        where('orgId', '==', selectedOrgId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const userList = snapshot.docs.map(snapDoc => ({
        id: snapDoc.id,
        ...snapDoc.data()
      }));
      
      console.log(`📊 Loaded ${userList.length} users`);
      setUsers(userList);
      
    } catch (error) {
      console.error('🚨 Error loading users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId]);

  // Load CMT reference (names) and flattened members for fallback rendering/backfill
  useEffect(() => {
    const loadCmtReference = async () => {
      try {
        if (!selectedOrgId) return;

        // Map CMT IDs to names (fresh from Firestore)
        const cmts = await cmtService.getOrganizationCMTs(selectedOrgId);
        const map = {};
        cmts.forEach(c => { if (c?.id) map[c.id] = c.name || c.id; });
        setCmtIdToName(map);

        // Use fresh flattened members from Firestore org doc
        const flattened = await cmtService.getFlattenedMembers(selectedOrgId);
        setOrgFlattenedMembers(Array.isArray(flattened) ? flattened : []);
      } catch (err) {
        console.warn('CMT reference load failed:', err);
      }
    };
    loadCmtReference();
  }, [selectedOrgId]);

  const deriveAssignmentsForUser = useCallback((user) => {
    if (!user?.email || !Array.isArray(orgFlattenedMembers)) return [];
    const emailLower = (user.email || '').toLowerCase();
    const matches = orgFlattenedMembers.filter(m => m?.email === emailLower);
    if (matches.length === 0) return [];
    return matches.map(m => ({
      cmtId: m.cmtId,
      cmtName: cmtIdToName[m.cmtId] || m.cmtId || 'CMT',
      role: (m.cmtRole || m.role || m.assignedRole || m.assignmentRole || m.cmt_role || m.roleName || m.cmtFunctionalRole || m.functionalRole || 'member'),
      isActive: true,
    }));
  }, [orgFlattenedMembers, cmtIdToName]);

  const backfillAssignmentsIfMissing = useCallback(async (user, derived) => {
    try {
      if (!user?.id || !Array.isArray(derived) || derived.length === 0) return;
      const processed = backfilledUserIdsRef.current;
      if (processed.has(user.id)) return;
      processed.add(user.id);
      const userRef = doc(db, 'c3_users', user.id);
      await updateDoc(userRef, {
        cmtAssignments: derived,
        updatedAt: new Date(),
        cmtAssignmentsBackfilled: true,
      });
    } catch (err) {
      console.warn('Backfill assignments failed for user', user?.id, err);
    }
  }, []);

  // Load users when organization changes
  useEffect(() => {
    if (selectedOrgId) {
      loadUsers();
    }
  }, [selectedOrgId, loadUsers]);

  // Auto-refresh when parent signals (e.g., tab became active)
  useEffect(() => {
    if (selectedOrgId && refreshTick) {
      loadUsers();
    }
  }, [refreshTick, selectedOrgId, loadUsers]);

  /**
   * 📧 Send invitation to single user
   */
  const sendInvitation = async (user) => {
    try {
      setActionLoading(true);
      addInviting(user.id);
      
      console.log(`INVITE_START: ${user.email}`);

      // Ensure the Auth user exists via Cloud Function (safe; no session swap)
      let createResult = null;
      try {
        createResult = await cloudUserService.createUserForOrganization(
          user.email,
          user.fullName,
          selectedOrgId,
          user.assignedCmtIds || [],
          'other',
          { cmtAssignments: (user.assignedCmtIds || []).map(cmtId => ({ cmtId, cmtRole: user.cmtRole || user.role || null })) }
        );
        console.log('USER_CREATE_OK:', createResult);
      } catch (e) {
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('already') && msg.includes('another organization')) {
          // Prompt for transfer
          const confirmTransfer = await new Promise(resolve => {
            Modal.confirm({
              title: 'User exists in another organization',
              content: `Do you want to move ${user.fullName} to ${selectedOrgName} and send the invite?`,
              okText: 'Move and Invite',
              cancelText: 'Cancel',
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            });
          });

          if (!confirmTransfer) {
            throw e;
          }

          // Retry with override transfer
          const transferRes = await cloudUserService.createUserForOrganization(
            user.email,
            user.fullName,
            selectedOrgId,
            user.assignedCmtIds || [],
            'other',
            { overrideTransfer: true, cmtAssignments: (user.assignedCmtIds || []).map(cmtId => ({ cmtId, cmtRole: user.cmtRole || user.role || null })) }
          );
          console.log('USER_TRANSFER_OK:', transferRes);
          createResult = transferRes;
        } else if (msg.includes('already') && msg.includes('exists')) {
          console.warn('USER_CREATE_ALREADY_EXISTS: proceeding to invite');
        } else {
          throw e;
        }
      }

      // Send the real invitation email (password reset)
      await cloudUserService.sendInvitationEmail(
        user.email,
        user.fullName,
        selectedOrgName,
        'Admin User'
      );
      
      // Update user status using returned uid when available; fallback to email
      const createdUid = createResult?.user?.uid;
      if (createdUid) {
        await cloudUserService.updateInvitationStatus(createdUid, 'invited', {
          invitedBy: 'admin_user',
          invitedAt: new Date()
        });
      } else {
        await cloudUserService.updateInvitationStatusByEmail(user.email, 'invited', {
          invitedBy: 'admin_user',
          invitedAt: new Date()
        });
      }
      
      message.success(`Invitation sent to ${user.fullName}`);

      // Cooldown this user's Invite/Resend for 120s
      setCooldownUserIds(prev => new Set(prev).add(user.id));
      setTimeout(() => {
        setCooldownUserIds(prev => {
          const next = new Set(prev);
          next.delete(user.id);
          return next;
        });
      }, 120000);
      
      // Reload users to reflect status change
      await loadUsers();
      
    } catch (error) {
      console.error(`INVITE_SEND_FAIL: ${user.email}`, error);
      // Mark failure state for visibility (prefer uid, fallback to email)
      try {
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('too-many-requests')) {
          message.info('You’ve sent too many invites to this email recently. Please wait a bit and try again.');
          // Keep status as invited; do not mark as failed
        } else {
          await cloudUserService.updateInvitationStatusByEmail(user.email, 'invitation_failed', {
            invitedBy: 'admin_user',
            invitedAt: new Date(),
            lastInviteError: error?.message || String(error)
          });
        }
      } catch (_) {}
      message.error(`Failed to send invitation: ${error.message}`);
    } finally {
      setActionLoading(false);
      removeInviting(user.id);
    }
  };

  /**
   * 📧 Bulk invitation workflow
   */
  const handleBulkInvite = async () => {
    try {
      setBulkOperationLoading(true);
      
      const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));
      const eligibleUsers = selectedUsers.filter(user => 
        user.status === 'not_invited' || user.status === 'invitation_failed'
      );
      
      if (eligibleUsers.length === 0) {
        message.warning('No eligible users selected for invitation');
        return;
      }
      
      console.log(`📧 BULK INVITE: Sending to ${eligibleUsers.length} users`);
      
      const results = {
        successful: [],
        failed: []
      };
      
      // Process invitations sequentially to avoid overwhelming email service
      for (const [index, user] of eligibleUsers.entries()) {
        try {
          console.log(`INVITE_START ${index + 1}/${eligibleUsers.length}: ${user.email}`);
          addInviting(user.id);

          // Ensure user exists
          try {
            await cloudUserService.createUserForOrganization(
              user.email,
              user.fullName,
              selectedOrgId,
              user.assignedCmtIds || [],
              'other',
              { cmtAssignments: (user.assignedCmtIds || []).map(cmtId => ({ cmtId, cmtRole: user.cmtRole || user.role || null })) }
            );
            console.log('USER_CREATE_OK');
          } catch (e) {
            const msg = e?.message || '';
            if (msg.toLowerCase().includes('already') && msg.toLowerCase().includes('exists')) {
              console.warn('USER_CREATE_ALREADY_EXISTS: proceeding');
            } else {
              throw e;
            }
          }

          // Send invite
          await cloudUserService.sendInvitationEmail(
            user.email,
            user.fullName,
            selectedOrgName,
            'Admin User'
          );
          
          // Update using uid when possible; fallback to email
          try {
            const lookupRes = await cloudUserService.createUserForOrganization(
              user.email,
              user.fullName,
              selectedOrgId,
              user.assignedCmtIds || [],
              'other',
              { cmtAssignments: (user.assignedCmtIds || []).map(cmtId => ({ cmtId, cmtRole: user.cmtRole || user.role || null })) }
            );
            const uid = lookupRes?.user?.uid;
            if (uid) {
              await cloudUserService.updateInvitationStatus(uid, 'invited', {
                invitedBy: 'admin_user',
                invitedAt: new Date(),
                bulkInvite: true
              });
            } else {
              await cloudUserService.updateInvitationStatusByEmail(user.email, 'invited', {
                invitedBy: 'admin_user',
                invitedAt: new Date(),
                bulkInvite: true
              });
            }
          } catch {
            await cloudUserService.updateInvitationStatusByEmail(user.email, 'invited', {
              invitedBy: 'admin_user',
              invitedAt: new Date(),
              bulkInvite: true
            });
          }
          
          results.successful.push(user);
          // Cooldown per-user button
          setCooldownUserIds(prev => new Set(prev).add(user.id));
          setTimeout(() => {
            setCooldownUserIds(prev => {
              const next = new Set(prev);
              next.delete(user.id);
              return next;
            });
          }, 120000);
          
          // Small delay to prevent rate limiting
          if (index < eligibleUsers.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          
        } catch (error) {
          console.error(`INVITE_SEND_FAIL (bulk): ${user.email}`, error);
          try {
            const msg = (error?.message || '').toLowerCase();
            if (msg.includes('too-many-requests')) {
              message.info('You’ve sent too many invites to this email recently. Please wait a bit and try again.');
            } else {
              await cloudUserService.updateInvitationStatusByEmail(user.email, 'invitation_failed', {
                invitedBy: 'admin_user',
                invitedAt: new Date(),
                bulkInvite: true,
                lastInviteError: error?.message || String(error)
              });
            }
          } catch (_) {}
          results.failed.push({ user, error: error.message });
        }
        finally {
          removeInviting(user.id);
        }
      }
      
      // Show results
      if (results.successful.length > 0) {
        message.success(`Successfully sent ${results.successful.length} invitations`);
      }
      
      if (results.failed.length > 0) {
        message.error(`Failed to send ${results.failed.length} invitations`);
      }
      
      // Clear selection and reload
      setSelectedUserIds([]);
      setBulkInviteModalVisible(false);
      await loadUsers();
      
    } catch (error) {
      console.error('🚨 Error in bulk invite:', error);
      message.error(`Bulk invite failed: ${error.message}`);
    } finally {
      setBulkOperationLoading(false);
    }
  };

  /**
   * 🔄 Update user status (active/deactivated)
   */
  const updateUserStatus = async (userId, newStatus) => {
    try {
      setActionLoading(true);
      
      const userRef = doc(db, 'c3_users', userId);
      await updateDoc(userRef, {
        status: newStatus,
        updatedAt: new Date(),
        statusChangedBy: 'admin_user'
      });
      
      message.success(`User status updated to ${newStatus}`);
      await loadUsers();
      
    } catch (error) {
      console.error('🚨 Error updating user status:', error);
      message.error('Failed to update user status');
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * 🏷️ Render CMT Role Tags
   * Shows all CMT assignments for a user
   */
  const renderCmtRoles = (user) => {
    const stored = Array.isArray(user?.cmtAssignments) ? user.cmtAssignments : [];
    const derived = deriveAssignmentsForUser(user);

    const assignmentsToShow = derived.length > 0 ? derived : stored;

    if (!assignmentsToShow || assignmentsToShow.length === 0) {
      return <Tag color="default">No CMT assigned</Tag>;
    }

    // Backfill/repair when we have a better derived set than stored
    if (derived.length > 0) {
      const storedKey = JSON.stringify(stored.map(a => ({ cmtId: a.cmtId, role: a.role || a.cmtRole })));
      const derivedKey = JSON.stringify(derived.map(a => ({ cmtId: a.cmtId, role: a.role })));
      if (storedKey !== derivedKey) {
        backfillAssignmentsIfMissing(user, derived);
      }
    }

    return (
      <Space size={[0, 4]} wrap>
        {assignmentsToShow.map((assignment, index) => (
          <Tag
            key={`${assignment.cmtId || assignment.cmtName || 'cmt'}_${index}`}
            color="blue"
            style={{ marginBottom: '2px' }}
          >
            <Tooltip title={`${assignment.cmtName || cmtIdToName[assignment.cmtId] || assignment.cmtId}: ${assignment.role || assignment.cmtRole || 'member'}`}>
              {(assignment.cmtName || cmtIdToName[assignment.cmtId] || assignment.cmtId)}: {(assignment.role || assignment.cmtRole || 'member')}
            </Tooltip>
          </Tag>
        ))}
      </Space>
    );
  };

  /**
   * 🎯 Get status badge
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_invited': { color: 'default', text: 'Not Invited', icon: <ClockCircleOutlined /> },
      'invited': { color: 'processing', text: 'Invited', icon: <MailOutlined /> },
      'active': { color: 'success', text: 'Active', icon: <CheckCircleOutlined /> },
      'deactivated': { color: 'error', text: 'Deactivated', icon: <StopOutlined /> },
      'invitation_failed': { color: 'warning', text: 'Invite Failed', icon: <ExclamationCircleOutlined /> }
    };
    
    const config = statusConfig[status] || statusConfig['not_invited'];
    
    return (
      <Badge 
        status={config.color} 
        text={
          <span>
            {config.icon} {config.text}
          </span>
        }
      />
    );
  };

  /**
   * 📊 Filter users based on search and filters
   */
  const filteredUsers = users.filter(user => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        user.fullName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.cmtAssignments?.some(assignment => 
          assignment.cmtName?.toLowerCase().includes(searchLower) ||
          assignment.role?.toLowerCase().includes(searchLower)
        );
      
      if (!matchesSearch) return false;
    }
    
    // Status filter
    if (statusFilter !== 'all' && user.status !== statusFilter) {
      return false;
    }
    
    // CMT filter
    if (cmtFilter !== 'all') {
      const hasCmtAssignment = user.cmtAssignments?.some(assignment => 
        assignment.cmtId === cmtFilter
      );
      if (!hasCmtAssignment) return false;
    }
    
    return true;
  });

  /**
   * 📋 Table columns configuration
   */
  const columns = [
    {
      title: (
        <Checkbox
          indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedUserIds(filteredUsers.map(user => user.id));
            } else {
              setSelectedUserIds([]);
            }
          }}
          checked={selectedUserIds.length === filteredUsers.length && filteredUsers.length > 0}
        >
          Select
        </Checkbox>
      ),
      dataIndex: 'select',
      key: 'select',
      width: 80,
      render: (_, user) => (
        <Checkbox
          checked={selectedUserIds.includes(user.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedUserIds([...selectedUserIds, user.id]);
            } else {
              setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
            }
          }}
        />
      ),
    },
    {
      title: 'User',
      dataIndex: 'fullName',
      key: 'user',
      render: (_, user) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>
            <UserOutlined style={{ marginRight: '8px' }} />
            {user.fullName || 'Unknown Name'}
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>{user.email}</div>
          {user.phone && (
            <div style={{ color: '#666', fontSize: '12px' }}>📞 {user.phone}</div>
          )}
        </div>
      ),
    },
    {
      title: 'CMT Assignments',
      dataIndex: 'cmtAssignments',
      key: 'cmtAssignments',
      render: (_, user) => renderCmtRoles(user),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusBadge(status),
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => {
        if (!date) return 'Never';
        const updateDate = date.toDate ? date.toDate() : new Date(date);
        return updateDate.toLocaleDateString();
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => (
        <Space>
          {(user.status === 'not_invited' || user.status === 'invitation_failed') && (
              <Button
              icon={<MailOutlined />}
              size="small"
              onClick={() => sendInvitation(user)}
                loading={invitingUserIds.has(user.id)}
                disabled={cooldownUserIds.has(user.id)}
              title="Send invitation email"
            >
              Invite
            </Button>
          )}
          
          {user.status === 'invited' && (
              <Button
              icon={<MailOutlined />}
              size="small"
              onClick={() => sendInvitation(user)}
                loading={invitingUserIds.has(user.id)}
                disabled={cooldownUserIds.has(user.id)}
              title="Resend invitation email"
            >
              Resend
            </Button>
          )}
          
          {user.status === 'active' && (
            <Button
              icon={<StopOutlined />}
              size="small"
              danger
              onClick={() => updateUserStatus(user.id, 'deactivated')}
              loading={actionLoading}
              title="Deactivate user"
            >
              Deactivate
            </Button>
          )}
          
          {user.status === 'deactivated' && (
            <Button
              icon={<CheckCircleOutlined />}
              size="small"
              type="primary"
              onClick={() => updateUserStatus(user.id, 'active')}
              loading={actionLoading}
              title="Reactivate user"
            >
              Reactivate
            </Button>
          )}
        </Space>
      ),
    },
  ];

  /**
   * 📊 Get unique CMT options for filter
   */
  const cmtOptions = React.useMemo(() => {
    const cmtMap = new Map();
    
    users.forEach(user => {
      if (user.cmtAssignments) {
        user.cmtAssignments.forEach(assignment => {
          if (assignment.cmtId && assignment.cmtName) {
            cmtMap.set(assignment.cmtId, assignment.cmtName);
          }
        });
      }
    });
    
    return Array.from(cmtMap.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [users]);

  /**
   * 📊 Get eligible users for bulk invite
   */
  const eligibleForInvite = selectedUserIds.filter(userId => {
    const user = users.find(u => u.id === userId);
    return user && (user.status === 'not_invited' || user.status === 'invitation_failed');
  }).length;

  if (!selectedOrgId) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <UserOutlined style={{ fontSize: '48px', color: '#ccc' }} />
        <div style={{ marginTop: '16px', color: '#666' }}>
          Select an organization to manage users
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Controls */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <Space wrap>
              <Search
                placeholder="Search users, emails, or CMT roles..."
                allowClear
                onSearch={setSearchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
              
              <Select
                placeholder="Filter by status"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: 150 }}
              >
                <Option value="all">All Statuses</Option>
                <Option value="not_invited">Not Invited</Option>
                <Option value="invited">Invited</Option>
                <Option value="active">Active</Option>
                <Option value="deactivated">Deactivated</Option>
              </Select>
              
              <Select
                placeholder="Filter by CMT"
                allowClear
                value={cmtFilter}
                onChange={setCmtFilter}
                style={{ width: 200 }}
              >
                <Option value="all">All CMTs</Option>
                {cmtOptions.map(cmt => (
                  <Option key={cmt.value} value={cmt.value}>{cmt.label}</Option>
                ))}
              </Select>
            </Space>
          </div>
          
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadUsers}
              loading={loading}
            >
              Refresh
            </Button>
            
            {selectedUserIds.length > 0 && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => setBulkInviteModalVisible(true)}
                disabled={eligibleForInvite === 0}
              >
                Invite Selected ({eligibleForInvite})
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {/* Statistics */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
              {filteredUsers.length}
            </div>
            <div style={{ color: '#666' }}>Total Users</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
              {filteredUsers.filter(u => u.status === 'active').length}
            </div>
            <div style={{ color: '#666' }}>Active</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
              {filteredUsers.filter(u => u.status === 'not_invited').length}
            </div>
            <div style={{ color: '#666' }}>Not Invited</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
              {filteredUsers.filter(u => u.status === 'invited').length}
            </div>
            <div style={{ color: '#666' }}>Invited</div>
          </div>
        </div>
      </Card>

      {/* Users Table */}
      <Card>
        <Table
          dataSource={filteredUsers}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Bulk Invite Modal */}
      <Modal
        title="Bulk Invitation"
        open={bulkInviteModalVisible}
        onCancel={() => setBulkInviteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setBulkInviteModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="invite"
            type="primary"
            loading={bulkOperationLoading}
            onClick={handleBulkInvite}
            disabled={eligibleForInvite === 0}
          >
            Send {eligibleForInvite} Invitations
          </Button>
        ]}
      >
        <div>
          <p>You are about to send invitation emails to {eligibleForInvite} users.</p>
          
          {eligibleForInvite !== selectedUserIds.length && (
            <Alert
              message="Note"
              description={`${selectedUserIds.length - eligibleForInvite} selected users are not eligible for invitation (already invited or active)`}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          
          <div style={{ marginTop: '16px' }}>
            <Text strong>Eligible users:</Text>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              {users
                .filter(user => selectedUserIds.includes(user.id) && 
                  (user.status === 'not_invited' || user.status === 'invitation_failed'))
                .slice(0, 10)
                .map(user => (
                  <li key={user.id}>{user.fullName} ({user.email})</li>
                ))
              }
              {eligibleForInvite > 10 && (
                <li>...and {eligibleForInvite - 10} more users</li>
              )}
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EnhancedUsersTabV2;
